import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';

import {
    ACCESS_TYPE,
    Fai,
    IPortability,
    Plan,
    ProductFactory,
    PromotionModel,
    PromotionsModel,
    PromotionsService,
    QuoteModel,
    Sim,
} from '@bytel/bytel-sales';
import { CheckoutStorageService } from '@bytel/checkout';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';
import { DataCsMaskDirective } from '@base/common/directives/data-mask.directive';


import { FaiService } from '@services/fai.services';
import { LandlineNumberService } from '@services/landline-number.service';
import { MainCartService } from '@services/main-cart.service';
import { IPortabilityControl, PortabilityService } from '@services/portability.service';

import { UNBUNDLING_TYPES } from '@interfaces/fai.interface';
import { LandlineNumber } from '@interfaces/landline-number.interface';
import { PhoneField } from '@interfaces/phone-field.interface';

import { LandlineNumberPipe } from '@shared/pipes/landline-number.pipe';

import { Observable, Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map } from 'rxjs/operators';

import { MandatoryFieldComponent } from "@shared/mandatory-field/mandatory-field.component";
import { PhoneRegex } from './phone-regex.class';

type RioFormGroup = FormGroup<{
    noTelephoneAPorter: FormControl<string | null>;
    codeRio: FormControl<string | null>;
}>;

@Component({
    selector: 'checkout-portability',
    templateUrl: './portability.component.html',
    styleUrl: 'portability.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        DataCyDirective,
        NgTemplateOutlet,
        NgClass,
        DataCsMaskDirective,
        LandlineNumberPipe,
        MandatoryFieldComponent
    ],
})
export class PortabilityComponent implements OnInit, OnDestroy, OnChanges {
    @Input() public cartForm: FormGroup;
    @Input() public isSubmitted: boolean;
    @Input() public plan: Plan;
    @Input() public quoteIndex: number = 0;

    public readonly RIO_REGEX: string = '^[a-z0-9+]{12}$';

    public portaForm: FormGroup<{
        rioTel?: RioFormGroup;
        eligPorta?: FormControl<string | null>;
        checked: FormControl<boolean | null>;
    }>;
    public rioTelForm: RioFormGroup;

    public phoneField: PhoneField = {
        hasEligibility: false,
        hide: false,
    };
    public valueChanges: Subscription = new Subscription();
    public isFai: boolean = false;
    public sim: Sim;
    public portaPromo: PromotionModel;
    public rioPromo: PromotionModel;
    public rioList: LandlineNumber[];
    public canShowApiError: boolean = false;

    private _portabilityId: string;

    constructor(
        private storageService: CheckoutStorageService,
        private faiService: FaiService,
        private cartService: MainCartService,
        private formBuilder: FormBuilder,
        private portabilityService: PortabilityService,
        private landlineNumberService: LandlineNumberService,
        private cd: ChangeDetectorRef,
    ) { }

    public ngOnInit(): void {
        this._initComponent();
        this.rioList = this.landlineNumberService.getRIOList();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.plan?.firstChange) {
            this._initComponent();
        }
    }

    public ngOnDestroy(): void {
        this.valueChanges.unsubscribe();
        this.cartForm.removeControl(this._getFormControlName());
    }

    public validatePhone(event: KeyboardEvent | ClipboardEvent): void {
        const allowedKeys = [...'0123456789'.split(''), 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

        if (event instanceof KeyboardEvent) {
            if ((event.metaKey || event.ctrlKey) && ['v', 'c', 'a'].includes(event.key.toLowerCase())) {
                return;
            }
            if (!allowedKeys.includes(event.key)) {
                event.preventDefault();
            }
        } else if (event instanceof ClipboardEvent) {
            const clipboardData = event.clipboardData || (window as any).clipboardData;
            const pastedData = clipboardData.getData('Text');
            if (!/^\d*$/.test(pastedData)) {
                event.preventDefault();
            }
        }
    }

    public toUpperCase(event: KeyboardEvent): void {
        const inputElement = event.target as HTMLInputElement;
        inputElement.value = inputElement.value.replace(/\s+/g, '').toUpperCase();
    }

    private _initComponent(): void {
        const quote: QuoteModel = this._getQuote();
        this.isFai = quote.isAcquisitionFix();
        this.sim = quote.getProductByType<Sim>('Sim');

        // recalcule promotions
        this._refreshPromo();
        this.cartService.refresh().subscribe(() => {
            this._refreshPromo();
        });

        this._initForm();
        this._addPortabilityToCartForm();
    }

    private _refreshPromo(): void {
        const allPlanPromos: PromotionsModel = PromotionsService.GetPromotionsForProduct(
            this.plan,
            this.cartService.cartModel.promotions,
        );
        this.portaPromo = allPlanPromos.automatic
            .concat(allPlanPromos.incitation)
            .find((promo) => promo.accessTypes?.includes(ACCESS_TYPE.PORTABILITE));
        this.rioPromo = allPlanPromos.automatic.find((promo) => !!promo.message.portability_other);
    }

    private _initForm(): void {
        let isActive: boolean = this._isPortaActive();
        let defaultRio = null;
        let phone = null;
        const quote = this._getQuote();
        if (quote.forms?.portability) {
            phone = quote.forms.portability.phoneNumber;
            defaultRio = quote.forms.portability.codeRio;
            isActive = quote.forms.portability.active;
        } else if (this.portaPromo) {
            isActive = true;
        } else {
            isActive = false;
        }

        this.portaForm = this.formBuilder.group({
            checked: this._initCheckedField(isActive),
        });

        if (isActive) {
            this._setFormControls(isActive, defaultRio, phone);
        }

        this.valueChanges = this.portaForm.get('checked').valueChanges.subscribe((next) => {
            this._setFormControls(next);
        });
    }
    private _addPortabilityToCartForm(): void {
        const formControlName = this._getFormControlName();
        this.cartForm.removeControl(formControlName);

        this.cartForm.addControl(formControlName, this.portaForm);

        this.portaForm.statusChanges.pipe(distinctUntilChanged()).subscribe((result) => {
            if (result === 'VALID') {
                this._save();
            }
        });
    }

    private _checkPortability(telNumber: string, codeRio: string): Observable<any> {
        let hasPortabilityStatus: boolean = false;
        return this.portabilityService.checkPortability(telNumber, codeRio).pipe(
            map((porta: IPortabilityControl) => {
                this._portabilityId = porta.idPorta;
                hasPortabilityStatus = true;
                this._save();
                this.portaForm.controls.eligPorta.setErrors({ apiError: null });
                return hasPortabilityStatus;
            }),
            catchError((data) => {
                const errors: ValidationErrors = {
                    apiError: data?.error?.message || 'Une erreur est survenue, veuillez réessayer',
                };
                return of(errors);
            }),
            finalize(() => {
                this.cd.markForCheck();
            }),
        );
    }

    private _phoneNumberValidators(): ValidatorFn[] {
        const regExp = new PhoneRegex(this._getQuote().context.id);
        return [Validators.required, Validators.maxLength(10), regExp.getRegExpValidator()];
    }

    private _rioValidators(): ValidatorFn[] {
        return [Validators.required, Validators.maxLength(12), this._getRioValidator()];
    }

    private _getRioValidator(): ValidatorFn {
        const regexp = new RegExp(this.RIO_REGEX, 'i');
        return (control: AbstractControl<string | null>): ValidationErrors | null => {
            if (control.value?.toUpperCase().startsWith('03P') || control.value?.toUpperCase().startsWith('03E')) {
                return {
                    rio: `Un forfait Bouygues Telecom est déjà associé à ce code RIO. Pour changer d\'offre, rendez-vous dans la rubrique <a target="_blank" class="link" href="/mon-compte">Mes Lignes</a> de votre espace client.`,
                };
            }
            if (!control.value?.toString().match(regexp)) {
                return { rio: 'Ce code RIO est incorrect. Merci de le vérifier et essayer à nouveau' };
            }

            if (ProductFactory.Is(this.plan, Fai) && control.value?.toLowerCase().startsWith('f4')) {
                return {
                    rio: `<p>Vous avez déjà une offre Bouygues Telecom sur ce numéro.</p>
<p>Vous souhaitez changer d'offre ? Cliquez sur "Je change d’offre" dans votre Espace Client, rubrique "Mon offre"</p>
<p>Vous souhaitez déménager ? Cliquez sur "Je déclare mon déménagement" dans votre Espace Client, rubrique "Mon offre"</p>`,
                };
            }
            return null;
        };
    }

    private _initCheckedField(isActive: boolean): FormControl {
        return new FormControl<boolean>(isActive, [Validators.required]);
    }

    private _isPortaActive(): boolean {
        if (this.isFai) {
            return false;
        }

        const promoConfigurator = this.storageService.get('promoConfigurator', false);
        return !promoConfigurator || promoConfigurator.portability;
    }

    private _setFormControls(isActive: boolean, rio: string = null, phone: string = null): void {
        if (!isActive) {
            this.portaForm.removeControl('rioTel');
            this.portaForm.removeControl('eligPorta');
        } else {
            this.rioTelForm = this.formBuilder.group({
                noTelephoneAPorter: new FormControl(phone, isActive ? this._phoneNumberValidators() : []),
                codeRio: new FormControl(rio, isActive ? this._rioValidators() : []),
            });

            this.portaForm.addControl('rioTel', this.rioTelForm);

            this.portaForm.addControl('eligPorta', new FormControl(null, [], [this._eligPortability()]));

            this.portaForm.get('rioTel').statusChanges.subscribe((next) => {
                this.canShowApiError = false
                if (next === 'VALID') {
                    this.portaForm.get('eligPorta').updateValueAndValidity();
                    this.canShowApiError = true
                }
            });
        }
        this._save();
    }

    private _save(): void {
        const quote: QuoteModel = this._getQuote();
        const isChecked: boolean = this.portaForm.get('checked').value;
        const portability: IPortability = {
            id: this._portabilityId || null,
            active: isChecked,
            phoneNumber: isChecked ? this._getPhoneFormControl().value : null,
            codeRio: isChecked ? this._getRioFormControl().value : null,
            typeDegroupage: isChecked ? UNBUNDLING_TYPES.TOTAL : null,
        };
        this.plan.portability = portability;
        const product = quote.getProductByType<Plan>(Plan);
        product.portability = portability;
        quote.addFormData({ portability });
        this.portabilityService.setPortability(isChecked, this.quoteIndex);
        this.cartService.refresh();
    }

    private _eligPortability(): AsyncValidatorFn {
        return (form): Observable<ValidationErrors> => {
            if (form.parent?.get('checked').value && form.parent?.get('rioTel').valid) {
                return this._checkPortability(this._getPhoneFormControl().value, this._getRioFormControl().value);
            }
            return of(null);
        };
    }

    private _getQuote(): QuoteModel {
        return this.cartService.getQuote(this.quoteIndex);
    }

    private _getFormControlName(): string {
        return `portability_${this.quoteIndex}`;
    }

    private _getRioFormControl(): AbstractControl {
        return this.portaForm.get('rioTel').get('codeRio');
    }

    private _getPhoneFormControl(): AbstractControl {
        return this.portaForm.get('rioTel').get('noTelephoneAPorter');
    }

    public getInputClass(controlName: 'noTelephoneAPorter' | 'codeRio'): string {
        const control = this.rioTelForm?.get(controlName);
        if (!control) return 'input is-focused';

        const hasError = this.hasError(controlName);
        const isValid = control.valid && (control.dirty || this.isSubmitted);

        return hasError ? 'input is-focused is-error' :
            isValid ? 'input is-focused is-success' :
                'input is-focused';
    }

    public hasError(controlName: 'noTelephoneAPorter' | 'codeRio'): boolean {
        const control = this.rioTelForm?.get(controlName);
        if (!control) return false;

        const hasFieldError = (control.dirty && control.invalid && control.touched) || (this.isSubmitted && control.invalid);
        const hasApiError = this.portaForm.get('eligPorta')?.errors?.apiError && this.canShowApiError;

        return hasFieldError || hasApiError;
    }

    public showIcon(controlName: 'noTelephoneAPorter' | 'codeRio'): boolean {
        const control = this.rioTelForm?.get(controlName);
        if (!control) return false;

        return (control.dirty || this.isSubmitted) && !this.portaForm.get('eligPorta')?.pending;
    }

    public getIconClass(controlName: 'noTelephoneAPorter' | 'codeRio'): string {
        const hasError = this.hasError(controlName);
        return hasError ? 'tri-exclamation-circle' : 'tri-check-circle';
    }
}
